import { Flex, Loader } from '@mantine/core';
import api from '@/api/api';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isJWTExpired } from '@/utils/functions';

interface AuthContextProps {
  token: string | null;
  setToken: (newToken: string | null) => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken_] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(true);

  const setToken = (newToken: string | null): void => {
    if (newToken && !isJWTExpired(newToken)) {
      localStorage.setItem('token', newToken);
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      setToken_(newToken);
    } else {
      localStorage.removeItem('token');
      api.defaults.headers.common.Authorization = undefined;
      setToken_(null);
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken && !isJWTExpired(storedToken)) {
      setToken(storedToken);
    } else {
      setToken(null);
    }
    setLoading(false);

    const responseInterceptor = api.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401) {
          setToken(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const contextValue = useMemo(() => ({ token, setToken }), [token]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ height: '100vh' }}>
        <Loader data-testid="Loader" />
      </Flex>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
